/**
 * PM Mini Featured Collection -- carrusel de una sola fila, scroll-snap
 * nativo + autoplay opcional. Independiente de Flickity/Multibanner:
 * ningún import, ninguna referencia cruzada.
 *
 * Idempotente: cada instancia [data-pm-mfc] se marca con
 * data-pm-mfc-bound tras inicializar, así que re-ejecuciones del script
 * (Theme Editor re-render de la sección) nunca duplican listeners.
 *
 * PM-INP (29-oct-2026): sin lecturas de layout en el camino caliente.
 * - Flechas: el estado inicio/fin sale de un IntersectionObserver sobre la
 *   primera y la última tarjeta (antes: scrollLeft/scrollWidth en cada
 *   scroll, que forzaba cálculo de layout).
 * - Paso del autoplay: se mide una vez y solo se vuelve a medir al cambiar
 *   el tamaño de la ventana (antes: getComputedStyle + getBoundingClientRect
 *   en cada avance).
 * - Autoplay: empieza cuando la página está libre después de "load" y se
 *   pausa mientras la sección no está en pantalla.
 */
(function () {
  'use strict';

  if (window.pmMfcInit) return;
  window.pmMfcInit = true;

  function isMobileViewport() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  function whenPageIdle(fn) {
    function run() {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(fn, { timeout: 3000 });
      } else {
        window.setTimeout(fn, 1500);
      }
    }
    if (document.readyState === 'complete') {
      run();
    } else {
      window.addEventListener('load', run, { once: true });
    }
  }

  function initInstance(root) {
    if (root.dataset.pmMfcBound === 'true') return;
    root.dataset.pmMfcBound = 'true';

    var track = root.querySelector('[data-pm-mfc-track]');
    var prevBtn = root.querySelector('[data-pm-mfc-prev]');
    var nextBtn = root.querySelector('[data-pm-mfc-next]');
    if (!track) return;

    var autoplayEnabled = root.dataset.autoplay === 'true';
    var intervalSeconds = parseFloat(root.dataset.autoplayInterval) || 5;
    var noAutoplayMobile = root.classList.contains('pm-mfc-wrapper--no-autoplay-mobile');
    var timer = null;
    var resumeTimeout = null;
    var stepCache = 0;
    var atStart = true;
    var atEndState = false;
    var inView = false;
    var pageReady = false;
    var hovered = false;

    function slideStep() {
      if (stepCache > 0) return stepCache;
      var slide = track.querySelector('.pm-mfc-slide');
      if (!slide) return track.clientWidth;
      var style = window.getComputedStyle(track);
      var gap = parseFloat(style.columnGap || style.gap || '0') || 0;
      stepCache = slide.getBoundingClientRect().width + gap;
      return stepCache;
    }

    function atEnd() {
      return atEndState;
    }

    function updateArrows() {
      if (prevBtn) prevBtn.disabled = atStart;
      if (nextBtn) nextBtn.disabled = atEndState;
    }

    function observeEdges() {
      var slides = track.querySelectorAll('.pm-mfc-slide');
      if (!slides.length || !('IntersectionObserver' in window)) {
        atStart = true;
        atEndState = slides.length <= 1;
        updateArrows();
        return;
      }
      var first = slides[0];
      var last = slides[slides.length - 1];
      var edgeObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var visible = entry.intersectionRatio >= 0.98;
          if (entry.target === first) atStart = visible;
          if (entry.target === last) atEndState = visible;
        });
        updateArrows();
      }, { root: track, threshold: [0, 0.98] });
      edgeObserver.observe(first);
      if (last !== first) edgeObserver.observe(last);
    }

    function scrollByStep(direction) {
      if (direction > 0 && atEnd()) {
        track.scrollTo({ left: 0, behavior: 'smooth' });
        return;
      }
      track.scrollBy({ left: slideStep() * direction, behavior: 'smooth' });
    }

    function shouldAutoplayNow() {
      if (!autoplayEnabled || !pageReady || !inView || hovered) return false;
      if (isMobileViewport() && noAutoplayMobile) return false;
      return true;
    }

    function stopAutoplay() {
      if (timer) {
        window.clearInterval(timer);
        timer = null;
      }
    }

    function startAutoplay() {
      stopAutoplay();
      if (!shouldAutoplayNow()) return;
      timer = window.setInterval(function () {
        if (atEnd()) {
          track.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          scrollByStep(1);
        }
      }, intervalSeconds * 1000);
    }

    function pauseAndScheduleResume() {
      stopAutoplay();
      if (resumeTimeout) window.clearTimeout(resumeTimeout);
      resumeTimeout = window.setTimeout(startAutoplay, intervalSeconds * 1000);
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        pauseAndScheduleResume();
        scrollByStep(-1);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        pauseAndScheduleResume();
        scrollByStep(1);
      });
    }

    ['pointerdown', 'touchstart', 'wheel'].forEach(function (evt) {
      track.addEventListener(evt, pauseAndScheduleResume, { passive: true });
    });

    root.addEventListener('mouseenter', function () {
      hovered = true;
      stopAutoplay();
    });
    root.addEventListener('mouseleave', function () {
      hovered = false;
      startAutoplay();
    });

    var resizeTimer = null;
    window.addEventListener('resize', function () {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(function () {
        stepCache = 0;
      }, 150);
    });

    if ('IntersectionObserver' in window) {
      var viewObserver = new IntersectionObserver(function (entries) {
        inView = entries[entries.length - 1].isIntersecting;
        if (inView) {
          startAutoplay();
        } else {
          stopAutoplay();
        }
      }, { threshold: 0.1 });
      viewObserver.observe(root);
    } else {
      inView = true;
    }

    observeEdges();
    whenPageIdle(function () {
      pageReady = true;
      startAutoplay();
    });
  }

  // PM 21-10-2026 (auditoria Ric, INP de la home): cada instancia se inicia
  // recien cuando esta a 300 px de entrar en pantalla, o si el foco del
  // teclado llega a ella antes. El funcionamiento no cambia.
  function observeInstance(root) {
    if (root.dataset.pmMfcBound === 'true') return;
    if (!('IntersectionObserver' in window)) {
      initInstance(root);
      return;
    }
    var observer = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) {
          observer.disconnect();
          initInstance(root);
          return;
        }
      }
    }, { rootMargin: '300px 0px' });
    observer.observe(root);
    root.addEventListener('focusin', function () {
      observer.disconnect();
      initInstance(root);
    }, { once: true });
  }

  function initAll() {
    document.querySelectorAll('[data-pm-mfc]').forEach(observeInstance);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme Editor: secciones nuevas/recargadas llegan después del load inicial.
  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector && event.target.querySelector('[data-pm-mfc]');
    if (root) initInstance(root);
  });
})();
